const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

export const toNumber = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const formatCurrency = (value?: number | string | null) =>
  currencyFormatter.format(toNumber(value));

export const formatNumber = (value?: number | string | null) =>
  numberFormatter.format(toNumber(value));

const asDate = (value?: Date | string | null): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const parseUtcAssumedDate = (value?: Date | string | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const normalizedValue = /(?:Z|[+-]\d{2}:\d{2})$/i.test(value) ? value : `${value}Z`;
  const date = new Date(normalizedValue);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateMMDDYYYY = (value?: Date | string | null, fallback = 'N/A'): string => {
  const date = asDate(value);
  if (!date) return fallback;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear());
  return `${month}/${day}/${year}`;
};

export const formatDateTimeMMDDYYYY = (
  value?: Date | string | null,
  opts?: { withSeconds?: boolean; fallback?: string },
): string => {
  const date = asDate(value);
  if (!date) return opts?.fallback ?? 'N/A';

  const datePart = formatDateMMDDYYYY(date, opts?.fallback ?? 'N/A');
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const hourPart = String(hours).padStart(2, '0');
  const timePart = opts?.withSeconds
    ? `${hourPart}:${minutes}:${seconds} ${ampm}`
    : `${hourPart}:${minutes} ${ampm}`;

  return `${datePart} ${timePart}`;
};

export const formatUtcAssumedDateTimeMMDDYYYY = (
  value?: Date | string | null,
  opts?: { withSeconds?: boolean; fallback?: string },
): string => {
  const date = parseUtcAssumedDate(value);
  if (!date) return opts?.fallback ?? 'N/A';
  return formatDateTimeMMDDYYYY(date, opts);
};

/**
 * Format a phone number for display.
 * US 10-digit: (XXX) XXX-XXXX; longer numbers get spaces every 3 digits.
 */
export const formatPhoneNumber = (value?: string | null): string => {
  if (value == null || value === '') return '—';
  const digitsOnly = value.replace(/\D/g, '');
  if (digitsOnly.length === 0) return '—';
  if (digitsOnly.length <= 3) return digitsOnly;
  if (digitsOnly.length <= 6) return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
  if (digitsOnly.length <= 10) return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  return digitsOnly.replace(/(\d{3})(?=\d)/g, '$1 ');
};
